/** Account, personal profile, backups, subscription and feature settings. */
import { motion } from 'framer-motion';
import type { HTMLMotionProps } from 'framer-motion';
import { useRef, useState } from 'react';
import type {
  AriaRole,
  CSSProperties,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
} from 'react';
import { navigate } from '../../app/hashRoute';
import { formatUnits } from '../../core/api';
import { formatPrice } from '../../core/formatPrice';
import { ImportFormatError, importDatabaseJson } from '../../core/repositories/transfer';
import type { ImportCounts } from '../../core/repositories/transfer';
import { tap } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import {
  BackupError,
  createEncryptedBackup,
  inspectEncryptedBackup,
  restoreEncryptedBackup,
} from '../backup';
import type { BackupInspection, BackupSummary, RestoreMode, RestoreResult } from '../backup';
import { isEncryptedBackup } from '../backup/container';
import { ProfileEditor } from '../account/ProfileEditor';
import { openAccount } from '../account/authMode';
import { SubscriptionPlanPage } from '../subscription';
import { quotaLabel } from '../subscription/api';
import { useEntitlements } from '../subscription/entitlements';
import { browserBackupDownloader, readFileBytes } from './backup';
import type { BackupDownloader } from './backup';
import {
  PAGE_TOP,
  SETTINGS_ENTRY_LINKS,
  settingsMeta,
  settingsBackupMeta,
  settingsEntryMeta,
} from './componentMeta';
import { useSettingsAccount } from './useSettingsAccount';
import { AppearanceCenter } from '../appearance/AppearanceCenter';
import styles from './SettingsPage.module.css';

type ComponentRole = NonNullable<ComponentMetaInput['role']>;
interface PendingImport {
  fileName: string;
  text: string;
}

interface PendingEncryptedImport {
  fileName: string;
  bytes: Uint8Array;
}

type BackupDialog = 'export' | 'import' | null;

function backupBytesText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '未知大小';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatBackupDate(timestamp: number): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '未知时间';
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function describeBackupSummary(summary: BackupSummary): string {
  const counts = summary.counts;
  return [
    `导出时间：${formatBackupDate(summary.exportedAt)}`,
    `导出账号：${summary.accountEmail || summary.accountId}`,
    `对话 ${counts.conversations} · 消息 ${counts.messages} · 联系人 ${counts.presets} · 朋友圈 ${counts.moments} · 图库 ${counts.gallery}`,
    `媒体 ${counts.media} 个 / ${backupBytesText(summary.mediaBytes)} · 流水 ${summary.ledger.included ? `${summary.ledger.count} 条` : '不可用'} · 语音 ${counts.ttsAudio} 条`,
    ...(summary.warnings.length === 0 ? [] : [`警告：${summary.warnings.join('；')}`]),
  ].join('\n');
}

function describeRestoreResult(result: RestoreResult): string {
  const counts = result.counts;
  const parts = [
    `恢复完成（${result.mode === 'merge' ? '合并恢复' : '完整替换'}）`,
    `对话 ${counts.conversations} · 消息 ${counts.messages} · 角色卡 ${counts.presets}`,
    `图库 ${counts.gallery} · 朋友圈 ${counts.moments} · 自动化待办 ${counts.automationEvents} · 记忆 ${counts.memories} · 提供商 ${counts.providers}`,
    `媒体 ${counts.media} · 流水 ${counts.ledger} · 语音 ${counts.ttsAudio}`,
    result.ttsPreferences === 'failed' ? '服务端 TTS 偏好未恢复' : '',
    ...result.warnings,
  ].filter((item) => item !== '');
  return parts.join('\n');
}

function backupErrorMessage(error: unknown): string {
  if (error instanceof BackupError) return error.message;
  if (error instanceof ImportFormatError) return `备份内容不合法：${error.message}`;
  return '备份操作失败，本机数据未改动';
}

function progressLabel(stage: string): string {
  const labels: Record<string, string> = {
    decrypt: '正在校验密码与备份完整性…',
    'safety-backup': '正在生成当前数据的安全备份…',
    snapshot: '正在记录回滚点…',
    'restore-db': '正在恢复聊天、图库与配置…',
    'restore-settings': '正在恢复本地设置与 API Key…',
    'restore-tts': '正在恢复语音缓存…',
    'restore-server-tts': '正在恢复服务端音色偏好…',
    rollback: '恢复失败，正在回滚…',
    done: '正在完成…',
  };
  return labels[stage] ?? stage;
}

function placementStyle(layout: BoxLayout, zIndex: number): CSSProperties {
  return {
    position: 'absolute',
    left: layout.x,
    top: layout.y - PAGE_TOP,
    width: layout.width,
    height: layout.height,
    zIndex,
  };
}

function useRegistered<T extends HTMLElement>(
  meta: ComponentMetaInput,
  fallbackRole: ComponentRole = 'container',
): {
  componentMeta: ReturnType<typeof buildComponentMeta>;
  ref: ReturnType<typeof useComponentMeta<T>>;
  style: CSSProperties;
} {
  const componentMeta = buildComponentMeta(meta, meta.role ?? fallbackRole);
  const ref = useComponentMeta<T>(componentMeta);
  const style = placementStyle(meta.layout, componentMeta.zIndex);
  return { componentMeta, ref, style };
}

interface RegisteredBlockProps {
  meta: ComponentMetaInput;
  className?: string;
  children?: ReactNode;
  ariaRole?: AriaRole;
  ariaModal?: boolean;
  ariaHidden?: boolean;
  surface?: 'page' | 'panel' | 'control' | 'overlay';
}

function RegisteredBlock({
  meta,
  className,
  children,
  ariaRole,
  ariaModal,
  ariaHidden,
  surface,
}: RegisteredBlockProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLDivElement>(meta);
  return (
    <div
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      role={ariaRole}
      aria-modal={ariaModal === true ? true : undefined}
      aria-label={ariaRole === undefined ? undefined : componentMeta.label}
      aria-hidden={ariaHidden}
      data-skin-surface={surface}
      style={style}
    >
      {children}
    </div>
  );
}

interface RegisteredHeadingProps {
  meta: ComponentMetaInput;
  className?: string;
  children: ReactNode;
}

function RegisteredHeading({ meta, className, children }: RegisteredHeadingProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLHeadingElement>(meta, 'text');
  return (
    <h1
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
    >
      {children}
    </h1>
  );
}
interface RegisteredButtonProps extends Omit<
  HTMLMotionProps<'button'>,
  'style' | 'children' | 'ref'
> {
  meta: ComponentMetaInput;
  children: ReactNode;
}

function RegisteredButton({
  meta,
  children,
  className,
  type = 'button',
  ...rest
}: RegisteredButtonProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLButtonElement>(meta, 'button');
  return (
    <motion.button
      {...rest}
      ref={ref}
      type={type}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      style={style}
      whileTap={tap.whileTap}
      transition={tap.transition}
    >
      {children}
    </motion.button>
  );
}

interface RegisteredFileInputProps {
  meta: ComponentMetaInput;
  disabled?: boolean;
  onChange: (file: File | null) => void;
}

interface BackupPickerHandle {
  getFile(): Promise<File>;
}

interface BackupPickerOptions {
  startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos';
  types?: { description?: string; accept: Record<string, string[]> }[];
  excludeAcceptAllOption?: boolean;
  multiple?: boolean;
}

type BackupPickerWindow = Window & {
  showOpenFilePicker?: (options?: BackupPickerOptions) => Promise<BackupPickerHandle[]>;
};

function pickerWasCanceled(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name?: unknown }).name === 'AbortError'
  );
}

/**
 * 备份文件选择。
 *
 * 优先使用 File System Access API：它能显式声明 `.zxbak` 类型并直接打开「下载」目录，
 * 不会落到系统记住的「照片和视频」筛选视图；不支持该 API 的浏览器（如 Firefox、
 * 移动端）回退到原生 `<input type="file">`，且不设置 accept，确保所有文件可见。
 */
function RegisteredFileInput({
  meta,
  disabled = false,
  onChange,
}: RegisteredFileInputProps): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  async function openPicker(): Promise<void> {
    if (disabled) return;
    const pickerWindow = window as BackupPickerWindow;
    if (typeof pickerWindow.showOpenFilePicker !== 'function') {
      inputRef.current?.click();
      return;
    }
    try {
      const handles = await pickerWindow.showOpenFilePicker({
        startIn: 'downloads',
        types: [
          {
            description: '栀歆备份',
            accept: { 'application/octet-stream': ['.zxbak', '.json'] },
          },
        ],
        excludeAcceptAllOption: false,
        multiple: false,
      });
      const handle = handles[0];
      if (handle === undefined) return;
      onChange(await handle.getFile());
    } catch (error) {
      if (pickerWasCanceled(error)) return;
      inputRef.current?.click();
    }
  }

  return (
    <>
      <RegisteredButton
        meta={meta}
        className={styles.backupButton}
        disabled={disabled}
        onClick={() => {
          void openPicker();
        }}
      >
        选择备份文件并还原
      </RegisteredButton>
      <input
        ref={inputRef}
        className={styles.fileInputHidden}
        type="file"
        aria-label="备份文件选择器"
        disabled={disabled}
        onChange={(event) => {
          const input = event.currentTarget;
          const file = input.files?.[0] ?? null;
          input.value = '';
          onChange(file);
        }}
      />
    </>
  );
}

interface RegisteredInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'style'> {
  meta: ComponentMetaInput;
}

function RegisteredInput({ meta, className, ...rest }: RegisteredInputProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLInputElement>(meta, 'input');
  return (
    <input
      {...rest}
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      style={style}
    />
  );
}

export interface SettingsPageProps {
  downloader?: BackupDownloader;
}
function describeCounts(counts: ImportCounts): string {
  return `恢复完成 · 对话 ${counts.conversations} · 消息 ${counts.messages} · 角色卡 ${counts.presets} · 提供商 ${counts.providers} · 记忆 ${counts.memories}`;
}

export function SettingsPage({
  downloader = browserBackupDownloader,
}: SettingsPageProps = {}): ReactElement {
  const account = useSettingsAccount();
  const entitlements = useEntitlements();
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<PendingImport | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [backupDialog, setBackupDialog] = useState<BackupDialog>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingEncrypted, setPendingEncrypted] = useState<PendingEncryptedImport | null>(null);
  const [inspection, setInspection] = useState<BackupInspection | null>(null);
  const [restoreMode, setRestoreMode] = useState<RestoreMode>('merge');
  const [progress, setProgress] = useState<string | null>(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);

  function resetBackupDialog(): void {
    if (busy) return;
    setBackupDialog(null);
    setPassword('');
    setConfirmPassword('');
    setPendingEncrypted(null);
    setInspection(null);
    setRestoreMode('merge');
    setProgress(null);
    setBackupError(null);
  }

  async function handleExportConfirm(): Promise<void> {
    if (account.user === null) return;
    if (password.length < 8) {
      setBackupError('备份密码至少需要 8 位');
      return;
    }
    if (password !== confirmPassword) {
      setBackupError('两次输入的密码不一致');
      return;
    }
    setBusy(true);
    setBackupError(null);
    setStatus(null);
    setProgress('正在收集数据并加密…');
    try {
      const backup = await createEncryptedBackup(password, {
        account: { id: account.user.id, email: account.user.email },
      });
      downloader.trigger(backup.filename, backup.blob);
      const warningText =
        backup.summary.warnings.length === 0
          ? ''
          : `；警告：${backup.summary.warnings.join('；')}`;
      setStatus(`已导出加密备份：${backup.filename}${warningText}`);
      setBackupDialog(null);
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      setBackupError(backupErrorMessage(error));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function handleBackupFile(file: File | null): Promise<void> {
    setStatus(null);
    setBackupError(null);
    if (file === null) return;
    setBusy(true);
    setStatus('正在读取备份文件…');
    try {
      let bytes: Uint8Array;
      try {
        bytes = await readFileBytes(file);
      } catch {
        setBackupError('读取备份文件失败，请重新选择');
        return;
      }
      if (isEncryptedBackup(bytes)) {
        setStatus(null);
        setPendingImport(null);
        setPendingEncrypted({ fileName: file.name, bytes });
        setInspection(null);
        setPassword('');
        setConfirmPassword('');
        setRestoreMode('merge');
        setBackupDialog('import');
        return;
      }
      let text: string;
      try {
        text = new TextDecoder().decode(bytes);
      } catch {
        setBackupError('读取备份文件失败，请重新选择');
        return;
      }
      try {
        JSON.parse(text);
      } catch {
        setBackupError('所选文件不是合法的加密备份或旧版 JSON，已取消恢复，本机数据未改动');
        return;
      }
      setStatus(null);
      setPendingEncrypted(null);
      setPendingImport({ fileName: file.name, text });
    } finally {
      setBusy(false);
    }
  }

  async function handleInspectBackup(): Promise<void> {
    if (pendingEncrypted === null || account.user === null) return;
    if (password === '') {
      setBackupError('请输入备份密码');
      return;
    }
    setBusy(true);
    setBackupError(null);
    setProgress('正在校验密码与备份完整性…');
    try {
      const result = await inspectEncryptedBackup(pendingEncrypted.bytes, password);
      if (result.summary.accountId !== account.user.id) {
        setInspection(null);
        setBackupError('这份备份属于其他账号，当前账号无法恢复');
        return;
      }
      setInspection(result);
      setBackupError(null);
    } catch (error) {
      setInspection(null);
      setBackupError(backupErrorMessage(error));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function handleRestoreConfirm(): Promise<void> {
    if (inspection === null || pendingEncrypted === null || account.user === null) return;
    setBusy(true);
    setBackupError(null);
    setStatus(null);
    setProgress(restoreMode === 'replace' ? '正在生成安全备份…' : '正在恢复备份…');
    try {
      const result = await restoreEncryptedBackup(pendingEncrypted.bytes, password, {
        userId: account.user.id,
        email: account.user.email,
        mode: restoreMode,
        downloadSafetyBackup: (filename, blob) => downloader.trigger(filename, blob),
        onProgress: (stage) => setProgress(progressLabel(stage)),
      });
      setStatus(describeRestoreResult(result));
      setBackupDialog(null);
      setPassword('');
      setConfirmPassword('');
      setPendingEncrypted(null);
      setInspection(null);
      setRestoreMode('merge');
    } catch (error) {
      setBackupError(backupErrorMessage(error));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  async function handleImportConfirm(): Promise<void> {
    if (pendingImport === null) return;
    setBusy(true);
    try {
      const counts = await importDatabaseJson(pendingImport.text);
      setStatus(describeCounts(counts));
      setBackupError(null);
    } catch (error) {
      setBackupError(backupErrorMessage(error));
    } finally {
      setBusy(false);
      setPendingImport(null);
    }
  }

  const fullPageOpen = appearanceOpen || planOpen;

  return (
    <RegisteredBlock meta={settingsMeta('page.settings.root')} className={styles.page} surface="page">
      <div
        className={styles.mainLayer}
        aria-hidden={fullPageOpen ? true : undefined}
        data-settings-main={fullPageOpen ? 'hidden' : 'visible'}
      >
        <RegisteredButton
          meta={settingsMeta('page.settings.appearance.entry')}
          className={styles.appearanceEntry}
          onClick={() => {
            setPlanOpen(false);
            setAppearanceOpen(true);
          }}
        >
          界面/外观
        </RegisteredButton>
        <div className={styles.scrollArea}>
        <RegisteredHeading meta={settingsMeta('page.settings.title')} className={styles.pageTitle}>
          设置
        </RegisteredHeading>
        {account.user === null ? (
          <>
            <RegisteredButton
              meta={settingsMeta('page.settings.auth.login')}
              className={styles.authButton}
              onClick={() => openAccount('login')}
            >
              登录
            </RegisteredButton>
            <RegisteredButton
              meta={settingsMeta('page.settings.auth.register')}
              className={styles.authButton}
              onClick={() => openAccount('register')}
            >
              注册
            </RegisteredButton>
            {account.error ? (
              <RegisteredBlock
                meta={settingsMeta('page.settings.account.error')}
                className={styles.backupError}
                ariaRole="alert"
              >
                {account.error}
              </RegisteredBlock>
            ) : null}
          </>
        ) : (
          <>
            <RegisteredBlock
              meta={settingsMeta('page.settings.account.card')}
              className={styles.card}
              ariaHidden
              surface="panel"
            />
            <RegisteredBlock
              meta={settingsMeta('page.settings.account.email')}
              className={styles.email}
            >
              {account.user.email}
            </RegisteredBlock>
            <RegisteredBlock
              meta={settingsMeta('page.settings.account.balance')}
              className={styles.balance}
            >
              {account.wallet
                ? `余额 ${formatPrice(formatUnits(account.wallet.balanceUnits))} ${account.wallet.unit}`
                : account.busy
                  ? '余额加载中…'
                  : '余额暂不可用'}
            </RegisteredBlock>
            <RegisteredButton
              meta={settingsMeta('page.settings.account.profile')}
              className={styles.entryButton}
              onClick={() => setEditingUserId(account.user!.id)}
            >
              个人资料
            </RegisteredButton>
            <RegisteredButton
              meta={settingsMeta('page.settings.subscription.entry')}
              className={styles.subscriptionButton}
              onClick={() => {
                setAppearanceOpen(false);
                setPlanOpen(true);
              }}
            >
              <SubscriptionText id="label" className={styles.subscriptionTitle}>
                官方订阅
              </SubscriptionText>
              <SubscriptionText id="value" className={styles.subscriptionValue}>
                {entitlements.active
                  ? entitlements.subscriptions.map((subscription) => subscription.planName).join(' + ')
                  : '未订阅 · 仅回复消息'}
              </SubscriptionText>
              <SubscriptionText id="hint" className={styles.subscriptionHint}>
                {entitlements.active
                  ? `文字 ${quotaLabel(entitlements.quota.textTokens)} · 图片 ${quotaLabel(entitlements.quota.images)} · 语音 ${quotaLabel(entitlements.quota.ttsChars)}`
                  : '当前套餐不支持自动冒泡、自动生图与自动语音'}
              </SubscriptionText>
            </RegisteredButton>
            <RegisteredBlock
              meta={settingsEntryMeta('page.settings.entries.card')}
              className={styles.card}
              ariaHidden
            />
            <RegisteredBlock
              meta={settingsEntryMeta('page.settings.entries.title')}
              className={styles.sectionLabel}
            >
              功能入口
            </RegisteredBlock>
            {SETTINGS_ENTRY_LINKS.map((entry) => (
              <RegisteredButton
                key={entry.id}
                meta={settingsEntryMeta('page.settings.entries.' + entry.id)}
                className={styles.entryButton}
                onClick={() => navigate(entry.path)}
              >
                {entry.label}
              </RegisteredButton>
            ))}
            <RegisteredBlock
              meta={settingsBackupMeta('page.settings.backup.card')}
              className={styles.card}
              ariaHidden
              surface="panel"
            />
            <RegisteredBlock
              meta={settingsBackupMeta('page.settings.backup.title')}
              className={styles.sectionLabel}
            >
              数据备份 / 恢复
            </RegisteredBlock>
            <RegisteredBlock
              meta={settingsBackupMeta('page.settings.backup.hint')}
              className={styles.backupHint}
            >
              导出加密 .zxbak（含聊天、图片、语音、API Key 与流水）；恢复请点下方按钮选择文件，需同一账号与密码。
            </RegisteredBlock>
            <RegisteredButton
              meta={settingsBackupMeta('page.settings.backup.exportButton')}
              className={styles.backupButton}
              disabled={busy}
              onClick={() => {
                setBackupError(null);
                setStatus(null);
                setPassword('');
                setConfirmPassword('');
                setPendingEncrypted(null);
                setInspection(null);
                setBackupDialog('export');
              }}
            >
              导出加密备份
            </RegisteredButton>
            <RegisteredFileInput
              meta={settingsBackupMeta('page.settings.backup.importInput')}
              disabled={busy}
              onChange={(file) => {
                void handleBackupFile(file);
              }}
            />
            {status !== null ? (
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.status')}
                className={styles.backupStatus}
                ariaRole="status"
              >
                {status}
              </RegisteredBlock>
            ) : null}
            {backupError !== null && backupDialog === null ? (
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.error')}
                className={styles.backupError}
                ariaRole="alert"
              >
                {backupError}
              </RegisteredBlock>
            ) : null}

          </>
        )}
        </div>
      </div>
      {appearanceOpen ? <AppearanceCenter onClose={() => setAppearanceOpen(false)} /> : null}
      {account.user !== null && (
        <>
          <SubscriptionPlanPage open={planOpen} onClose={() => setPlanOpen(false)} />
          {editingUserId === account.user.id ? (
            <ProfileEditor
              key={account.user.id}
              userId={account.user.id}
              onClose={() => setEditingUserId(null)}
            />
          ) : null}
          {backupDialog !== null ? (
            <>
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.dialog.backdrop')}
                className={styles.backdrop}
                ariaHidden
              surface="overlay"
              />
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.dialog')}
                className={styles.dialog}
                ariaRole="dialog"
                ariaModal
                surface="overlay"
              />
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.dialog.title')}
                className={styles.dialogTitle}
              >
                {backupDialog === 'export'
                  ? '导出加密备份'
                  : inspection === null
                    ? '输入备份密码'
                    : '确认还原'}
              </RegisteredBlock>
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.dialog.hint')}
                className={styles.dialogText}
              >
                {backupDialog === 'export'
                  ? '密码至少 8 位，不会保存在任何地方；写出后请自行妥善保管。'
                  : inspection === null
                    ? `文件：${pendingEncrypted?.fileName ?? ''}；校验通过前不会写入任何数据。`
                    : '校验已通过。请选择恢复方式后开始。'}
              </RegisteredBlock>
              {backupDialog === 'export' ||
              (backupDialog === 'import' && inspection === null) ? (
                <>
                  <RegisteredBlock
                    meta={settingsBackupMeta('page.settings.backup.dialog.passwordLabel')}
                    className={styles.dialogLabel}
                  >
                    备份密码
                  </RegisteredBlock>
                  <RegisteredInput
                    meta={settingsBackupMeta('page.settings.backup.dialog.passwordInput')}
                    className={styles.dialogInput}
                    type="password"
                    autoComplete={backupDialog === 'export' ? 'new-password' : 'current-password'}
                    minLength={backupDialog === 'export' ? 8 : undefined}
                    maxLength={128}
                    disabled={busy}
                    value={password}
                    onChange={(event) => setPassword(event.currentTarget.value)}
                  />
                </>
              ) : null}
              {backupDialog === 'export' ? (
                <>
                  <RegisteredBlock
                    meta={settingsBackupMeta('page.settings.backup.dialog.confirmLabel')}
                    className={styles.dialogLabel}
                  >
                    确认密码
                  </RegisteredBlock>
                  <RegisteredInput
                    meta={settingsBackupMeta('page.settings.backup.dialog.confirmInput')}
                    className={styles.dialogInput}
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={128}
                    disabled={busy}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.currentTarget.value)}
                  />
                  <RegisteredBlock
                    meta={settingsBackupMeta('page.settings.backup.dialog.exportWarning')}
                    className={styles.dialogWarning}
                  >
                    文件包含 API Key、参考音频与全部聊天 / 图库数据。密码遗失后无法恢复。
                  </RegisteredBlock>
                </>
              ) : null}
              {backupDialog === 'import' && inspection !== null ? (
                <>
                  <RegisteredBlock
                    meta={settingsBackupMeta('page.settings.backup.dialog.summary')}
                    className={styles.backupSummary}
                  >
                    {describeBackupSummary(inspection.summary)}
                  </RegisteredBlock>
                  <RegisteredBlock
                    meta={settingsBackupMeta('page.settings.backup.dialog.modeLabel')}
                    className={styles.dialogLabel}
                  >
                    恢复方式
                  </RegisteredBlock>
                  <RegisteredButton
                    meta={settingsBackupMeta('page.settings.backup.dialog.modeMerge')}
                    className={
                      restoreMode === 'merge'
                        ? `${styles.modeButton} ${styles.modeButtonActive}`
                        : styles.modeButton
                    }
                    aria-pressed={restoreMode === 'merge'}
                    disabled={busy}
                    onClick={() => setRestoreMode('merge')}
                  >
                    合并恢复
                  </RegisteredButton>
                  <RegisteredButton
                    meta={settingsBackupMeta('page.settings.backup.dialog.modeReplace')}
                    className={
                      restoreMode === 'replace'
                        ? `${styles.modeButton} ${styles.modeButtonActive}`
                        : styles.modeButton
                    }
                    aria-pressed={restoreMode === 'replace'}
                    disabled={busy}
                    onClick={() => setRestoreMode('replace')}
                  >
                    完整替换
                  </RegisteredButton>
                  {restoreMode === 'replace' ? (
                    <RegisteredBlock
                      meta={settingsBackupMeta('page.settings.backup.dialog.replaceWarning')}
                      className={styles.dialogWarning}
                    >
                      完整替换会先自动下载当前数据的安全备份，再清空本机聊天、图库、角色卡、提供商、记忆与当前账号语音数据。
                    </RegisteredBlock>
                  ) : (
                    <RegisteredBlock
                      meta={settingsBackupMeta('page.settings.backup.dialog.mergeHint')}
                      className={styles.dialogHint}
                    >
                      合并恢复会保留本机独有记录；与备份同 id 的记录由备份覆盖，API Key 与设置同样覆盖。
                    </RegisteredBlock>
                  )}
                </>
              ) : null}
              {progress !== null ? (
                <RegisteredBlock
                  meta={settingsBackupMeta('page.settings.backup.dialog.progress')}
                  className={styles.backupStatus}
                  ariaRole="status"
                >
                  {progress}
                </RegisteredBlock>
              ) : null}
              {backupError !== null ? (
                <RegisteredBlock
                  meta={settingsBackupMeta('page.settings.backup.dialog.error')}
                  className={styles.backupError}
                  ariaRole="alert"
                >
                  {backupError}
                </RegisteredBlock>
              ) : null}
              <RegisteredButton
                meta={settingsBackupMeta('page.settings.backup.dialog.cancel')}
                className={styles.dialogSecondary}
                disabled={busy}
                onClick={resetBackupDialog}
              >
                取消
              </RegisteredButton>
              <RegisteredButton
                meta={settingsBackupMeta('page.settings.backup.dialog.confirm')}
                className={styles.dialogPrimary}
                disabled={busy || (pendingEncrypted === null && backupDialog === 'import')}
                onClick={() => {
                  if (backupDialog === 'export') void handleExportConfirm();
                  else if (inspection === null) void handleInspectBackup();
                  else void handleRestoreConfirm();
                }}
              >
                {busy
                  ? '处理中…'
                  : backupDialog === 'export'
                    ? '导出'
                    : inspection === null
                      ? '下一步'
                      : '开始还原'}
              </RegisteredButton>
            </>
          ) : null}
          {pendingImport !== null ? (
            <>
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.confirm.backdrop')}
                className={styles.backdrop}
                ariaHidden
              />
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.confirm.dialog')}
                className={styles.dialog}
                ariaRole="dialog"
                ariaModal
              />
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.confirm.title')}
                className={styles.dialogTitle}
              >
                恢复这份备份？
              </RegisteredBlock>
              <RegisteredBlock
                meta={settingsBackupMeta('page.settings.backup.confirm.text')}
                className={styles.dialogText}
              >
                {`将按 id 覆盖写入「${pendingImport.fileName}」，已有同 id 记录会被替换。`}
              </RegisteredBlock>
              <RegisteredButton
                meta={settingsBackupMeta('page.settings.backup.confirm.cancel')}
                className={styles.dialogSecondary}
                disabled={busy}
                onClick={() => setPendingImport(null)}
              >
                取消
              </RegisteredButton>
              <RegisteredButton
                meta={settingsBackupMeta('page.settings.backup.confirm.confirm')}
                className={styles.dialogPrimary}
                disabled={busy}
                onClick={() => {
                  void handleImportConfirm();
                }}
              >
                确认恢复
              </RegisteredButton>
            </>
          ) : null}
        </>
      )}
    </RegisteredBlock>
  );
}

function SubscriptionText({
  id,
  children,
  className,
}: {
  id: string;
  children: ReactNode;
  className?: string;
}): ReactElement {
  const meta = settingsMeta('page.settings.subscription.' + id);
  const complete = buildComponentMeta(meta, 'text');
  const ref = useComponentMeta<HTMLSpanElement>(complete);
  const parent = settingsMeta('page.settings.subscription.entry').layout;
  return (
    <span
      ref={ref}
      data-ui-id={complete.id}
      data-ui-role={complete.role}
      className={className}
      style={{
        position: 'absolute',
        left: meta.layout.x - parent.x,
        top: meta.layout.y - parent.y,
        width: meta.layout.width,
        height: meta.layout.height,
      }}
    >
      {children}
    </span>
  );
}
